import { Avatar, Popover, ScrollArea, Select, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { Link, useNavigate } from "@tanstack/react-router";
import { Check, ChevronDown, Plus } from "lucide-react";
import { useState } from "react";

import type { Brand, TeamRole } from "../../api/generated/model";
import ellipse from "../../ui/assets/ellipse-image.png";
import { managesBrands } from "../permissions";
import { SECTIONS } from "./nav";
import { useCurrentSection, useNavBrands } from "./nav-links";
import classes from "./shell.module.css";

interface BrandSelectProps {
  teamId: string;
  role: TeamRole;
  open: boolean;
  onNavigate: (() => void) | undefined;
}

/** A brand's round mark: its logo (the signed `logo_url`), else live's neutral ellipse (2.4). */
function BrandMark({ brand, size }: { brand: Brand; size: number }) {
  return (
    <Avatar
      size={size}
      radius="xl"
      src={brand.logo_url ?? ellipse}
      alt={brand.name}
      name={brand.name}
      color="initials"
      classNames={{ image: classes.brandLogo }}
    />
  );
}

function BrandRow({ brand, selected }: { brand: Brand; selected: boolean }) {
  return (
    <span className={classes.brandOption} data-selected={selected || undefined}>
      <BrandMark brand={brand} size={18} />
      <span className={classes.brandName}>{brand.name}</span>
      {selected ? <Check size={16} className={classes.brandCheck} aria-hidden /> : null}
    </span>
  );
}

/**
 * The sidebar's brand: a select when open, a rail button with a popover when compact. Choosing
 * a brand opens the same section under it (a product or a job of the old brand has no meaning
 * under the new one), or the brand's page from a team page. The URL is the only state (F3).
 */
export function BrandSelect({ teamId, role, open, onNavigate }: BrandSelectProps) {
  const { brands, current, failed } = useNavBrands(teamId);
  const section = useCurrentSection();
  const navigate = useNavigate();
  const [popover, setPopover] = useState(false);

  const choose = (brandId: string) => {
    setPopover(false);
    onNavigate?.();
    const target = section === undefined ? undefined : SECTIONS.get(section)?.target;
    if (target?.scope === "brand") {
      const search = target.medium === undefined ? {} : { medium: target.medium };
      void navigate({ to: target.to, params: { teamId, brandId }, search });
    } else {
      void navigate({ to: "/teams/$teamId/brands/$brandId", params: { teamId, brandId } });
    }
  };

  if (failed) {
    return (
      <Text size="xs" c="var(--cai-color-text-muted)" className={classes.brandSelect}>
        Brands did not load
      </Text>
    );
  }
  if (brands === undefined) return null;
  if (brands.length === 0) {
    if (!managesBrands(role)) return null;
    const add = (
      <Link
        to="/teams/$teamId/brands/new"
        params={{ teamId }}
        className={open ? classes.brandOption : classes.brandRail}
        aria-label={open ? undefined : "Add a brand"}
        onClick={onNavigate}
      >
        <Plus size={18} aria-hidden />
        {open ? "Add a brand" : null}
      </Link>
    );
    return open ? add : <Tooltip label="Add a brand" position="right">{add}</Tooltip>;
  }

  if (open) {
    return (
      <Select
        aria-label="Brand"
        className={classes.brandSelect}
        classNames={{ option: classes.selectOption }}
        data={brands.map((brand) => ({ value: brand.id, label: brand.name }))}
        value={current?.id ?? null}
        onChange={(value) => {
          if (value !== null && value !== current?.id) choose(value);
        }}
        allowDeselect={false}
        withCheckIcon={false}
        leftSection={current === undefined ? null : <BrandMark brand={current} size={24} />}
        // Live's select shows a down chevron, not Mantine's up-down selector.
        rightSection={<ChevronDown size={16} aria-hidden />}
        rightSectionPointerEvents="none"
        renderOption={({ option, checked }) => {
          const brand = brands.find((each) => each.id === option.value);
          return brand === undefined ? (
            option.label
          ) : (
            <BrandRow brand={brand} selected={checked === true} />
          );
        }}
      />
    );
  }

  return (
    <Popover opened={popover} onChange={setPopover} position="right-start" offset={12}>
      <Popover.Target>
        <UnstyledButton
          className={classes.brandRail}
          aria-label={`Brand: ${current?.name ?? ""}`}
          aria-expanded={popover}
          onClick={() => setPopover((value) => !value)}
        >
          <Tooltip label={current?.name ?? "Brand"} position="right" disabled={popover}>
            <span>
              {current === undefined ? null : <BrandMark brand={current} size={24} />}
            </span>
          </Tooltip>
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown className={classes.brandPopover}>
        <ScrollArea.Autosize mah={300} type="auto" offsetScrollbars scrollbarSize={5}>
          {brands.map((brand) => (
            <UnstyledButton key={brand.id} w="100%" onClick={() => choose(brand.id)}>
              <BrandRow brand={brand} selected={brand.id === current?.id} />
            </UnstyledButton>
          ))}
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}
